import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[8%]" },
  { key: "title", name: "Titulli", sort_value: "title", className: "w-[34%]" },
  { key: "type", name: "Tipi", sort_value: "type", className: "w-[18%]" },
  { key: "recipients", name: "Marrësit", sort_value: "recipients", className: "w-[12%]", align: "right" },
  { key: "time", name: "Koha", sort_value: "created_at", className: "w-[20%]" },
];

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Tirane",
    });
  } catch {
    return "—";
  }
};

const TypePill = ({ type }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
    {type || "—"}
  </span>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-48 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-12 bg-gray-200 rounded ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-3 w-28 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function NotificationTable({
  notifications,
  isLoading,
  params = {},
  onSort,
  onRowClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} ${
                      col.align === "right" ? "text-right" : "text-left"
                    } px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!notifications || notifications.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : notifications?.map((n) => (
                  <tr
                    key={n.notification_id}
                    onClick={() => onRowClick?.(n)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{n.notification_id}
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="font-medium text-gray-900 truncate block max-w-[420px]">
                        {n.title || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <TypePill type={n.type_name} />
                    </td>
                    <td className="px-6 py-3.5 text-right text-gray-700 tabular-nums">
                      {n.total_users ?? "—"}
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">
                      {formatDateTime(n.received_at)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!notifications || notifications.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          notifications?.map((n) => (
            <button
              key={n.notification_id}
              type="button"
              onClick={() => onRowClick?.(n)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{n.title || "—"}</div>
                  <div className="text-xs text-gray-500 tabular-nums">#{n.notification_id}</div>
                </div>
                <TypePill type={n.type_name} />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span className="tabular-nums">{formatDateTime(n.received_at)}</span>
                <span className="tabular-nums">{n.total_users ?? "—"} marrës</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
